// ============================================================================
// vpn/VpnManagement.tsx — режим «Управление» (только админ / роль vpn_manage):
// профили и доступ по ролям, проверка соединения, конфиг/QR, журнал сессий,
// политика протоколов, SSH-доступ. Перенесено из VpnPage БЕЗ изменения
// поведения; настройки VPN — SSoT сервера (VpnService).
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TabBar } from '@/components/ui/TabBar';
import { getSDK } from '@/store/sdkStore';
import { useToastStore } from '@/store/toastStore';
import { confirmDialog, promptDialog } from '@/lib/dialogs';
import { createLogger } from '@/lib/logger';
import { friendlyError } from '@/lib/errors';
import { eventBusHub } from '@/lib/eventBusHub';
import { VpnProfileForm, type VpnProfileFormData } from './VpnProfileForm';
import { VpnSshBlock } from './VpnSshBlock';
import { VpnAccessBlock } from './VpnAccessBlock';
import { VpnConfigBlock } from './VpnConfigBlock';
import { VpnSettingsTab } from './VpnSettingsTab';
import { VpnSessionsTable, type VpnSessionRow } from './VpnSessionsTable';
import { parseVpnExtra, serializeVpnExtra, vpnProtoDef, type VpnFieldTarget } from './protocolFields';

const log = createLogger('VpnManagement');

interface VpnProfileListItem extends VpnProfileFormData {
  extra: string;
}

interface RoleItem {
  id: string;
  name: string;
}

interface VpnProtocolInfo {
  code: string;
  label: string;
  kind: string; // store | extended
}

const EMPTY: VpnProfileFormData = {
  id: '', code: '', name: '', synonym: '', protocol: 'wireguard', server: '', port: 51820,
  publicKey: '', address: '', dns: '1.1.1.1', allowedIps: '0.0.0.0/0, ::/0', isActive: true,
};

// Авто-код профиля из имени (латиница): транслит RU→EN + слаг.
const TRANSLIT: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z', и: 'i', й: 'y', к: 'k',
  л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'c',
  ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
};

function codeFromName(name: string): string {
  const lat = name
    .toLowerCase()
    .split('')
    .map((ch) => (TRANSLIT[ch] !== undefined ? TRANSLIT[ch] : ch))
    .join('');
  return lat.replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40) || 'vpn';
}

function uniqueCode(base: string, taken: Set<string>): string {
  if (!taken.has(base)) return base;
  let i = 2;
  while (taken.has(`${base}_${i}`)) i++;
  return `${base}_${i}`;
}

export function VpnManagement() {
  const [tab, setTab] = useState('profiles');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [profiles, setProfiles] = useState<VpnProfileListItem[]>([]);
  const [roles, setRoles] = useState<RoleItem[]>([]);
  const [sessions, setSessions] = useState<VpnSessionRow[]>([]);
  const [form, setForm] = useState<VpnProfileFormData>(EMPTY);
  const [extra, setExtra] = useState<Record<string, string>>({});
  const [extraInvalid, setExtraInvalid] = useState(false);
  const [psk, setPsk] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [password, setPassword] = useState('');
  const [protocols, setProtocols] = useState<VpnProtocolInfo[]>([]);
  const [extendedEnabled, setExtendedEnabled] = useState(false);

  const showError = (m: string) => useToastStore.getState().showError(m);
  const showSuccess = (m: string) => useToastStore.getState().showSuccess(m);

  const loadProfiles = useCallback(async (): Promise<VpnProfileListItem[]> => {
    setLoading(true);
    try {
      const r = await getSDK().vpn.listVpnProfiles({ activeOnly: false });
      const items = (r.items || []).map((p) => ({
        id: p.id, code: p.code, name: p.name, synonym: p.synonym, protocol: p.protocol,
        server: p.server, port: Number(p.port), publicKey: p.publicKey, address: p.address,
        dns: p.dns, allowedIps: p.allowedIps, isActive: !!p.isActive,
        extra: p.extra || '',
      }));
      setProfiles(items);
      return items;
    } catch (e) {
      log.error('load vpn profiles', { error: e });
      showError(friendlyError(e));
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const r = await getSDK().vpn.getVpnSettings({});
      setProtocols((r.settings?.protocols || []).map((p) => ({ code: p.code, label: p.label, kind: p.kind })));
      setExtendedEnabled(!!r.settings?.extendedEnabled);
    } catch (e) {
      log.error('load vpn settings', { error: e });
      showError(friendlyError(e));
    }
  }, []);

  const loadSessions = useCallback(async () => {
    try {
      const r = await getSDK().vpn.listVpnSessions({});
      setSessions((r.items || []).map((s) => ({
        id: s.id, profileCode: s.profileCode, userId: s.userId, status: s.status,
        clientInfo: s.clientInfo, connectedAt: s.connectedAt, disconnectedAt: s.disconnectedAt,
      })));
    } catch (e) {
      log.error('load vpn sessions', { error: e });
      showError(friendlyError(e));
    }
  }, []);

  useEffect(() => {
    void loadProfiles();
    void loadSettings();
    getSDK().auth.getRoles({})
      .then((r) => setRoles((r.items || []).map((x) => ({ id: x.id, name: x.name }))))
      .catch((e) => { log.error('load roles', { error: e }); setRoles([]); });
  }, [loadProfiles, loadSettings]);

  useEffect(() => {
    if (tab === 'sessions') void loadSessions();
  }, [tab, loadSessions]);

  const resetForm = useCallback(() => {
    setForm(EMPTY);
    setExtra({});
    setExtraInvalid(false);
    setPsk('');
    setPrivateKey('');
    setPassword('');
  }, []);

  const applyProfile = useCallback((p: VpnProfileListItem) => {
    setForm(p);
    const map = parseVpnExtra(p.extra);
    setExtra(map ?? {});
    setExtraInvalid(map === null);
    setPsk('');
    setPrivateKey('');
    setPassword('');
  }, []);

  const setExtraValue = useCallback((key: string, value: string) => {
    setExtra((prev) => ({ ...prev, [key]: value }));
  }, []);

  // ЖИВОЕ ОБНОВЛЕНИЕ (шина): vpn.profile.created|updated|deleted — список и форма
  // обновляются без перезагрузки; vpn.settings.updated — политика протоколов.
  useEffect(() => {
    const unsub = eventBusHub.subscribe((ev) => {
      const topic = ev.topic || '';
      if (!topic.startsWith('vpn.')) return;
      if (topic === 'vpn.settings.updated') {
        void loadSettings();
        return;
      }
      if (topic.startsWith('vpn.profile.')) {
        void loadProfiles();
        if (topic === 'vpn.profile.deleted' && String(ev.payload?.code || '') === form.code) {
          resetForm();
        }
      }
    });
    return unsub;
  }, [form.code, loadProfiles, loadSettings, resetForm]);

  const createProfile = async () => {
    setTab('profiles');
    const name = await promptDialog(
      'Имя профиля VPN (код сгенерируется автоматически):',
      '',
      'Новый профиль',
    );
    const n = (name || '').trim();
    if (!n) return;
    const code = uniqueCode(codeFromName(n), new Set(profiles.map((p) => p.code)));
    setBusy(true);
    try {
      await getSDK().vpn.saveVpnProfile({
        code, name: n, synonym: '', protocol: 'wireguard', server: '', port: 51820,
        publicKey: '', address: '', dns: '1.1.1.1', allowedIps: '0.0.0.0/0, ::/0',
        isActive: true, psk: '', privateKey: '', password: '',
      });
      showSuccess(`Профиль «${n}» создан`);
      const items = await loadProfiles();
      const saved = items.find((p) => p.code === code);
      if (saved) applyProfile(saved);
    } catch (e) {
      showError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveProfile = async () => {
    const name = form.name.trim();
    if (!name) { showError('Укажите имя профиля'); return; }
    const code = form.id ? form.code : uniqueCode(form.code.trim() || codeFromName(name), new Set(profiles.map((p) => p.code)));
    // Сохраняются ТОЛЬКО поля, релевантные выбранному протоколу (состав — protocolFields).
    const def = vpnProtoDef(form.protocol);
    const targets = new Set<VpnFieldTarget>(def.fields.map((f) => f.target));
    const extraOut: Record<string, string> = {};
    for (const f of def.fields) {
      if (f.target === 'extra' && f.extraKey) extraOut[f.extraKey] = extra[f.extraKey] || '';
    }
    setBusy(true);
    try {
      await getSDK().vpn.saveVpnProfile({
        code, name, synonym: form.synonym,
        protocol: form.protocol,
        server: targets.has('server') ? form.server.trim() : '',
        port: targets.has('port') ? Math.round(Number(form.port) || 0) : 0,
        publicKey: targets.has('publicKey') ? form.publicKey : '',
        address: targets.has('address') ? form.address : '',
        dns: targets.has('dns') ? form.dns : '',
        allowedIps: targets.has('allowedIps') ? form.allowedIps : '',
        extra: serializeVpnExtra(extraOut),
        isActive: form.isActive, psk, privateKey, password,
      });
      showSuccess('Профиль сохранён');
      setPsk('');
      setPrivateKey('');
      setPassword('');
      const items = await loadProfiles();
      const saved = items.find((p) => p.code === code);
      if (saved) applyProfile(saved);
    } catch (e) {
      showError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const removeProfile = async () => {
    if (!form.code) return;
    if (!await confirmDialog(`Удалить VPN-профиль «${form.name || form.code}»?`)) return;
    setBusy(true);
    try {
      await getSDK().vpn.deleteVpnProfile({ code: form.code });
      showSuccess('Профиль удалён');
      resetForm();
      await loadProfiles();
    } catch (e) {
      showError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveSettings = async () => {
    setBusy(true);
    try {
      await getSDK().vpn.setVpnSettings({ extendedEnabled });
      showSuccess('Политика протоколов сохранена');
      await loadSettings();
    } catch (e) {
      showError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const protocolLabel = (code: string) =>
    protocols.find((p) => p.code === code)?.label || code;

  const TABS = useMemo(() => ([
    { id: 'profiles', label: 'Профили' },
    { id: 'sessions', label: 'Сессии' },
    { id: 'settings', label: 'Настройки' },
  ]), []);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b p-3 shrink-0">
        <TabBar tabs={TABS} active={tab} onChange={setTab} group="vpn-manage" />
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void loadProfiles()} disabled={loading || busy}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Обновить
          </Button>
          <Button size="sm" onClick={createProfile} disabled={busy}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Новый профиль
          </Button>
        </div>
      </div>

      {tab === 'profiles' && (
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="px-3 pt-2 shrink-0">
            <VpnSshBlock />
          </div>
          <div className="flex-1 min-h-0 flex">
            <div className="w-64 shrink-0 border-r overflow-auto">
              {profiles.length === 0 && !loading && (
                <div className="p-3 text-xs text-muted-foreground">Профилей нет</div>
              )}
              {profiles.map((p) => (
                <button
                  key={p.code}
                  type="button"
                  onClick={() => applyProfile(p)}
                  className={`w-full text-left px-3 py-2 border-b hover:bg-accent/5 ${form.code === p.code ? 'bg-accent/10' : ''}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm truncate">{p.synonym || p.name}</span>
                    {!p.isActive && <span className="text-[10px] text-muted-foreground">выкл</span>}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate">{protocolLabel(p.protocol)} · {p.code}</div>
                </button>
              ))}
            </div>

            <div className="flex-1 min-h-0 overflow-auto p-3 space-y-3">
              <VpnProfileForm
                form={form}
                setForm={setForm}
                extra={extra}
                extraInvalid={extraInvalid}
                setExtraValue={setExtraValue}
                psk={psk}
                setPsk={setPsk}
                privateKey={privateKey}
                setPrivateKey={setPrivateKey}
                password={password}
                setPassword={setPassword}
                protocols={protocols}
                extendedEnabled={extendedEnabled}
                busy={busy}
                onSave={() => void saveProfile()}
                onDelete={() => void removeProfile()}
              />
              <VpnAccessBlock profileCode={form.code} roles={roles} busy={busy} />
              <VpnConfigBlock profileCode={form.code} busy={busy} protocolLabel={protocolLabel} />
            </div>
          </div>
        </div>
      )}

      {tab === 'sessions' && (
        <div className="flex-1 min-h-0 overflow-auto p-3">
          <VpnSessionsTable sessions={sessions} />
        </div>
      )}

      {tab === 'settings' && (
        <VpnSettingsTab
          protocols={protocols}
          extendedEnabled={extendedEnabled}
          setExtendedEnabled={setExtendedEnabled}
          busy={busy}
          onSave={() => void saveSettings()}
        />
      )}
    </div>
  );
}
